// RightSurface — host for the shell's right slot. ONE right surface at a time: the Ask AI drawer
// (top-bar triggered) takes the edge and the Context Inspector yields; closing Ask AI restores it.
// This is how VW adds Ask AI without a third rail — it shares the existing right region.
//
// Connections / AI Context remain app-scoped. A resolved project also has a shared Project Context
// inspector, without mounting unrelated app modules. Ask AI
// is a GLOBAL top-bar drawer and still works at any scope. When neither applies the slot returns
// null, so AppShellLayout collapses the right region (main takes full width — no empty gutter).
import { lazy, Suspense, type ReactElement } from "react";
import { useNavigate } from "@tanstack/react-router";
import { InspectorEdgeTab } from "@perlab/ui";
import { ZARVIS_APP_ID } from "@perlab/registry";

import { useShellStore } from "../state/shellStore";
import { useContextChain } from "../state/ContextChain";
import { useMountScope } from "../state/useMountScope";
import { useOpenApps } from "../state/openApps";
import { resolveAppTarget } from "../components/appTarget";
import { Inspector } from "./Inspector";
import { ZarvisLazyBoundary } from "./ZarvisLazyBoundary";

const AskAiDrawer = lazy(() => import("./AskAiDrawer").then((module) => ({
  default: module.AskAiDrawer,
})));

function DrawerPlaceholder({ failed = false }: { failed?: boolean }): ReactElement {
  const close = useShellStore((state) => state.closeAskAi);
  return (
    <aside aria-label="Ask AI" className="flex h-full w-full flex-col bg-[var(--color-surface-base)] p-4">
      <div className="flex items-center justify-between">
        <span>Ask AI</span>
        <button type="button" onClick={close} aria-label="Close Ask AI">Close</button>
      </div>
      <p role={failed ? "alert" : "status"} className="mt-4 text-sm">
        {failed ? "Ask AI could not be loaded. Refresh the page to try again." : "Loading Ask AI..."}
      </p>
    </aside>
  );
}

export function RightSurface(): ReactElement | null {
  const navigate = useNavigate();
  const askAiOpen = useShellStore((s) => s.askAiOpen);
  const closeAskAi = useShellStore((s) => s.closeAskAi);
  const openApp = useOpenApps((s) => s.openApp);
  const { appId, workspaceId } = useContextChain();
  const { scopeNodeId, currentLevel, levelPending, levelError } = useMountScope();
  const projectContext = Boolean(scopeNodeId && currentLevel === "project" && !levelPending && !levelError);

  // Zarvis is already the foreground work surface; never render a second copy in
  // the right slot or expose an unrelated Context Inspector beside it.
  if (appId === ZARVIS_APP_ID) return null;
  if (askAiOpen) {
    const mountToTray = (): void => {
      if (!workspaceId || !scopeNodeId) return;
      openApp({
        appId: ZARVIS_APP_ID,
        workspaceId,
        scopeNodeId,
        name: "Zarvis",
        icon: "sparkles",
      });
      closeAskAi();
      navigate(resolveAppTarget({
        workspaceId,
        scopeNodeId,
        appId: ZARVIS_APP_ID,
      }) as never);
    };
    return (
      <ZarvisLazyBoundary fallback={<DrawerPlaceholder failed />}>
        <Suspense fallback={<DrawerPlaceholder />}>
          <AskAiDrawer onMountToTray={mountToTray} />
        </Suspense>
      </ZarvisLazyBoundary>
    );
  }
  if (appId) return <Inspector projectContext={projectContext} />;
  if (projectContext) return <Inspector projectContext projectOnly />;
  return null;
}

// The hidden-Inspector reveal tab only exists when there's an app or resolved project Inspector
// and Ask AI isn't owning the right edge.
export function RightEdgeTab(): ReactElement | null {
  const askAiOpen = useShellStore((s) => s.askAiOpen);
  const { appId } = useContextChain();
  const { scopeNodeId, currentLevel, levelPending, levelError } = useMountScope();
  const projectContext = Boolean(scopeNodeId && currentLevel === "project" && !levelPending && !levelError);
  if (askAiOpen || (!appId && !projectContext) || appId === ZARVIS_APP_ID) return null;
  return <InspectorEdgeTab />;
}
